import { ROUTES } from 'app/utils/constants/paths';
import useAnalytics from 'core/hooks/useAnalytics';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { EVENT_NAMES } from '../constants/eventNames';
import { SESSION_STATUS } from '../constants/sessionStatus';
import practiceService from '../services/practiceService';
import { mapSessionTypeToOrigin } from '../utils/challengeTypeUtils';

/**
 * usePracticeExitFlow - Owns leaving the practice view from the header X (TANAI-1994).
 * A student-created, in-progress session gets a confirmation dialog offering two exits:
 * "discard" (PATCH the discard endpoint, then leave) or "save for later" (just leave,
 * the current behavior). Tutor sessions — and the session-complete "¡Hecho!" button —
 * skip the dialog and exit straight away via `exitPractice`.
 *
 * The analytics payload, the discard guard and the route live here so the view stays
 * pure JSX. On a discard failure the dialog is kept open and nothing is reset or
 * navigated, so the student can retry.
 */

type UsePracticeExitFlowParams = {
  resetPracticeSession: () => void;
  sessionId?: string;
  sessionStatus?: string;
  sessionType?: string;
  subject?: string;
};

type UsePracticeExitFlowReturn = {
  closeExitDialog: () => void;
  discardPractice: () => Promise<void>;
  exitPractice: () => void;
  isDiscarding: boolean;
  isExitDialogOpen: boolean;
  requestExit: () => void;
  saveForLater: () => void;
};

function usePracticeExitFlow({
  resetPracticeSession,
  sessionId,
  sessionStatus,
  sessionType,
  subject,
}: UsePracticeExitFlowParams): UsePracticeExitFlowReturn {
  const history = useHistory();
  const { trackEvent } = useAnalytics();
  const [isDiscarding, setIsDiscarding] = useState<boolean>(false);
  const [isExitDialogOpen, setIsExitDialogOpen] = useState<boolean>(false);

  const exitPractice = useCallback(() => {
    resetPracticeSession();
    history.replace(ROUTES.TODAY);
  }, [history, resetPracticeSession]);

  const requestExit = useCallback(() => {
    if (mapSessionTypeToOrigin(sessionType) === 'student') {
      setIsExitDialogOpen(true);
      return;
    }
    exitPractice();
  }, [exitPractice, sessionType]);

  const closeExitDialog = useCallback(() => {
    setIsExitDialogOpen(false);
  }, []);

  const discardPractice = useCallback(async () => {
    // The ui Dialog disables its footer while loading, but guard anyway so a second
    // trigger never fires a duplicate PATCH.
    if (isDiscarding) return;

    setIsDiscarding(true);

    try {
      await practiceService.discardSession(sessionId);
      trackEvent(EVENT_NAMES.ACTIVITY_SESSION_DISCARDED, {
        practice_session_id: sessionId,
        type: sessionType,
        subject,
      });
      exitPractice();
    } catch {
      // Keep the dialog open so the student can retry; do not reset or navigate.
      // The service already logged the failure through its error handler.
      setIsDiscarding(false);
    }
  }, [exitPractice, isDiscarding, sessionId, sessionType, subject, trackEvent]);

  const saveForLater = useCallback(() => {
    trackEvent(EVENT_NAMES.ACTIVITY_SESSION_SAVED_FOR_LATER, {
      practice_session_id: sessionId,
      type: sessionType,
      subject,
    });
    exitPractice();
  }, [exitPractice, sessionId, sessionType, subject, trackEvent]);

  // Safety net: a deep link that somehow resolves to an already discarded session must
  // never be playable, so bail straight back to "Today".
  useEffect(() => {
    if (sessionStatus === SESSION_STATUS.DISCARDED) {
      exitPractice();
    }
  }, [exitPractice, sessionStatus]);

  return {
    closeExitDialog,
    discardPractice,
    exitPractice,
    isDiscarding,
    isExitDialogOpen,
    requestExit,
    saveForLater,
  };
}

export default usePracticeExitFlow;
